import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import styles from "../EventDashboardV2.module.scss"
import StatCard from "./StatCard"
import type { InvitationStatusFilter, ReminderStatusFilter } from "../constants/filters"
import {
  INVITATION_FILTERS,
  matchesInvitationFilter,
  matchesStatusWithFailureType,
} from "../constants/filters"
import { fetchAllEventContacts } from "../utils/fetchAllEventContacts"

interface RemindersOverviewProps {
  invitationFilter: InvitationStatusFilter
  reminderFilter: ReminderStatusFilter
  onInvitationFilterChange: (filter: InvitationStatusFilter) => void
  onReminderFilterChange: (filter: ReminderStatusFilter) => void
  reminderType?: number
}

interface ReminderFields {
  status: string
  failureType: string
}

const getFieldsForType = (reminderType: number): ReminderFields => {
  switch (reminderType) {
    case 1:
      return {
        status: "rsvp_reminder_status",
        failureType: "rsvp_reminder_failure_type",
      }
    case 2:
      return {
        status: "qr_reminder_status",
        failureType: "qr_reminder_failure_type",
      }
    default:
      return {
        status: "reminder_status",
        failureType: "reminder_failure_type",
      }
  }
}

const isQrReminderEligible = (guest: any): boolean =>
  guest.status === 1 || guest.status === 4

interface ReminderStats {
  sent: number
  notSent: number
  failedContact: number
  failedSystem: number
  total: number
}

const RemindersOverview: React.FC<RemindersOverviewProps> = ({
  invitationFilter,
  reminderFilter,
  onInvitationFilterChange,
  onReminderFilterChange,
  reminderType = 0,
}) => {
  const { t } = useTranslation()
  const { event_id } = useParams()
  const { status: statusField, failureType: failureTypeField } =
    getFieldsForType(reminderType)
  const isQrReminder = reminderType === 2
  const activeInv =
    INVITATION_FILTERS.find((f) => f.id === invitationFilter) ?? INVITATION_FILTERS[0]
  const qrStatusNumber =
    isQrReminder && activeInv.id === "all" ? "1,4" : activeInv.statusNumber

  const [stats, setStats] = useState<ReminderStats>({
    sent: 0,
    notSent: 0,
    failedContact: 0,
    failedSystem: 0,
    total: 0,
  })

  useEffect(() => {
    let cancelled = false

    const loadStats = async () => {
      try {
        const guests = await fetchAllEventContacts({
          sort: "",
          order: "",
          searchValue: "",
          id: Number(event_id),
          statusNumber: qrStatusNumber,
          waStatusNumber: activeInv.waStatusNumber,
          reminderNumber: "",
        })

        const scoped = guests.filter(
          (g: any) =>
            matchesInvitationFilter(g, activeInv) &&
            (!isQrReminder || isQrReminderEligible(g)),
        )

        if (cancelled) return

        setStats({
          sent: scoped.filter((g: any) => g[statusField] === "sent").length,
          failedContact: scoped.filter((g: any) =>
            matchesStatusWithFailureType(
              g,
              statusField,
              failureTypeField,
              "failed_contact",
            ),
          ).length,
          failedSystem: scoped.filter((g: any) =>
            matchesStatusWithFailureType(
              g,
              statusField,
              failureTypeField,
              "failed_system",
            ),
          ).length,
          notSent: scoped.filter((g: any) =>
            matchesStatusWithFailureType(
              g,
              statusField,
              failureTypeField,
              "not_sent",
            ),
          ).length,
          total: scoped.length,
        })
      } catch {
        if (!cancelled) {
          setStats({
            sent: 0,
            notSent: 0,
            failedContact: 0,
            failedSystem: 0,
            total: 0,
          })
        }
      }
    }

    loadStats()
    return () => {
      cancelled = true
    }
  }, [
    event_id,
    qrStatusNumber,
    activeInv,
    statusField,
    failureTypeField,
    isQrReminder,
  ])

  const { sent, notSent, failedContact, failedSystem, total } = stats
  const sentPercent = total > 0 ? Math.round((sent / total) * 100) : 0

  const selectFilter = (filter: ReminderStatusFilter) => {
    const next = reminderFilter === filter ? "all" : filter
    onReminderFilterChange(next)
    onInvitationFilterChange("all")
  }

  return (
    <div>
      <h3 className={styles.sectionTitle}>{t("eventDashboardV2.labels.overview")}</h3>
      <div className={styles.overviewGrid}>
        <div className={`${styles.overviewRow} ${styles.overviewRowFour}`}>
          <StatCard
            value={sent}
            total={total}
            label={t("eventDashboardV2.filters.sent")}
            valueColor="Green"
            progress={sentPercent}
            onClick={() => selectFilter("sent")}
          />
          <StatCard
            value={notSent}
            total={total}
            label={t("eventDashboardV2.filters.not_sent")}
            valueColor="Orange"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => selectFilter("not_sent")}
          />
          <StatCard
            value={failedContact}
            total={total}
            label={t("eventDashboardV2.filters.failed_contact")}
            valueColor="Red"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => selectFilter("failed_contact")}
          />
          <StatCard
            value={failedSystem}
            total={total}
            label={t("eventDashboardV2.filters.failed_system")}
            valueColor="Orange"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => selectFilter("failed_system")}
          />
        </div>
      </div>
    </div>
  )
}

export default RemindersOverview
